import { useMemo, useState } from 'react'
import EstoqueHttp from '../../../../services/api/https/estoque/Estoque.http'
import * as ProdutoHttp from '../../../../services/api/https/registration/produto.http'
import * as SystemHttp from '../../../../services/api/https/system/system.http'
import { parseToMysqlFormat } from '../../../../services/util/date.service'
import { errorHandler } from '../../../../services/util/error.service'
import { getEmpresas } from '../../../../services/localstorage/localstorage.service'

export const LOAD_MESSAGE = {
  NONE: 'NONE',
  ESTOQUES: 'Carregando estoques',
  PRODUTOS: 'Carregando produtos',
  PESQUISA: 'Carregando saldo de estoque e movimentações',
  SALVAR: 'Criando movimentação',
}

const ZERO_DIFERENCA_ERROR =
  'Não é possível criar movimentação de acerto pois não há diferença entre Saldo Estoque e Saldo Movimentações'

function getHoraAtual() {
  const now = new Date()
  return `${`${now.getHours()}`.padStart(2, '0')}:${`${now.getMinutes()}`.padStart(2, '0')}`
}

function createInitialCreateMovimentacaoForm(result = null) {
  return {
    selectedEstoque: null,
    selectedPosicaoEstoque: null,
    selectedOperacaoEstoque: null,
    dataMovimentacao: new Date(),
    horaMovimentacao: getHoraAtual(),
    quantidade: result ? String(Math.abs(Number(result.diferenca) || 0)) : '',
  }
}

const INITIAL_STATE = {
  estoques: [],
  empresas: getEmpresas(),
  produtos: [],
  selectedEstoques: [],
  selectedEmpresa: null,
  selectedProduto: null,
  globalFilterEstoque: '',
  globalFilterMovimentacoes: '',
  loadingMessage: LOAD_MESSAGE.NONE,
  errorMessage: null,
  successMessage: null,
  result: null,
  showCreateMovimentacaoModal: false,
  showConfirmCriacaoModal: false,
  operacoesMovimentacao: [],
  posicoesMovimentacao: [],
  loadingOperacoesMovimentacao: false,
  loadingPosicoesMovimentacao: false,
  savingMovimentacao: false,
  createMovimentacaoForm: createInitialCreateMovimentacaoForm(),
}

function buildProdutos(produtos) {
  return produtos
    .map(produto => ({
      id: produto.id,
      product_code: produto.product_code,
      company_id: produto.company_id,
      name: `${produto.product_code} - ${produto.name}`,
      original_name: produto.name,
    }))
    .sort((a, b) => a.name.localeCompare(b.name))
}

function buildOperacoes(response) {
  const operacoes = response && response.operacoes_entrada_saida ? response.operacoes_entrada_saida : []

  return operacoes
    .filter(operacao => operacao.type === 'Entrada' || operacao.type === 'Saída')
    .map(operacao => ({
      ...operacao,
      label: `${operacao.name} - Grupo: ${operacao.grupo_de_operacao || ''}`,
    }))
    .sort((a, b) => a.label.localeCompare(b.label))
}

function resetCreateMovimentacaoState(extraState = {}) {
  return {
    showCreateMovimentacaoModal: false,
    showConfirmCriacaoModal: false,
    operacoesMovimentacao: [],
    posicoesMovimentacao: [],
    loadingOperacoesMovimentacao: false,
    loadingPosicoesMovimentacao: false,
    savingMovimentacao: false,
    createMovimentacaoForm: createInitialCreateMovimentacaoForm(),
    ...extraState,
  }
}

function validateCreateMovimentacaoForm(createMovimentacaoForm) {
  if (!createMovimentacaoForm.selectedEstoque) {
    return 'Selecione um estoque.'
  }

  if (!createMovimentacaoForm.selectedPosicaoEstoque) {
    return 'Selecione uma posição de estoque.'
  }

  if (!createMovimentacaoForm.selectedOperacaoEstoque) {
    return 'Selecione uma operação de estoque.'
  }

  if (!createMovimentacaoForm.dataMovimentacao) {
    return 'Selecione uma data de movimentação.'
  }

  if (!createMovimentacaoForm.horaMovimentacao || !/^\d{2}:\d{2}$/.test(createMovimentacaoForm.horaMovimentacao)) {
    return 'Informe uma hora de movimentação válida.'
  }

  if (!createMovimentacaoForm.quantidade || !Number(createMovimentacaoForm.quantidade)) {
    return 'Informe uma quantidade válida.'
  }

  return null
}

function getTipoOperacaoMovimentacao(result) {
  if (!result) {
    return null
  }

  const saldoEstoque = Number(result.saldo_total_estoque) || 0
  const saldoMovimentacoes = Number(result.saldo_final_movimentacoes) || 0

  if (saldoMovimentacoes < saldoEstoque) {
    return 'Entrada'
  }

  if (saldoMovimentacoes > saldoEstoque) {
    return 'Saída'
  }

  return null
}

export default function useSaldoMovimentacoesSaldoEstoque() {
  const [state, setState] = useState({ ...INITIAL_STATE })

  const localDispatch = useMemo(() => {
    return {
      resetState() {
        setState(prevState => ({
          ...prevState,
          loadingMessage: LOAD_MESSAGE.NONE,
          errorMessage: null,
        }))
      },

      clearSuccessMessage() {
        setState(prevState => ({
          ...prevState,
          successMessage: null,
        }))
      },

      async onInit(selectedTp) {
        if (!selectedTp || selectedTp === 0) {
          setState({
            ...INITIAL_STATE,
            empresas: getEmpresas(),
            errorMessage: 'Selecione um TP para utilizar esta tela.',
          })
          return
        }

        setState(prevState => ({
          ...prevState,
          loadingMessage: LOAD_MESSAGE.ESTOQUES,
          errorMessage: null,
          estoques: [],
          selectedEstoques: [],
          result: null,
          ...resetCreateMovimentacaoState(),
        }))

        try {
          const estoques = await EstoqueHttp.getEstoques(selectedTp)
          setState(prevState => ({
            ...prevState,
            estoques,
            loadingMessage: LOAD_MESSAGE.NONE,
          }))
        } catch (error) {
          setState(prevState => ({
            ...prevState,
            loadingMessage: LOAD_MESSAGE.NONE,
            errorMessage: errorHandler(error),
          }))
        }
      },

      async onEmpresaChange(selectedEmpresa) {
        setState(prevState => ({
          ...prevState,
          selectedEmpresa,
          selectedProduto: null,
          produtos: [],
          result: null,
          errorMessage: null,
          ...resetCreateMovimentacaoState(),
        }))

        if (!selectedEmpresa) {
          return
        }

        setState(prevState => ({
          ...prevState,
          loadingMessage: LOAD_MESSAGE.PRODUTOS,
        }))

        try {
          const response = await ProdutoHttp.getProdutos(selectedEmpresa.id)
          setState(prevState => ({
            ...prevState,
            produtos: buildProdutos(response.produtos),
            loadingMessage: LOAD_MESSAGE.NONE,
          }))
        } catch (error) {
          setState(prevState => ({
            ...prevState,
            loadingMessage: LOAD_MESSAGE.NONE,
            errorMessage: errorHandler(error),
          }))
        }
      },

      onChange(name, value) {
        setState(prevState => {
          const nextState = {
            ...prevState,
            [name]: value,
          }

          if (name === 'selectedEstoques' || name === 'selectedProduto') {
            nextState.result = null
            Object.assign(nextState, resetCreateMovimentacaoState())
          }

          return nextState
        })
      },

      onChangeCreateMovimentacao(name, value) {
        setState(prevState => ({
          ...prevState,
          createMovimentacaoForm: {
            ...prevState.createMovimentacaoForm,
            [name]: value,
          },
        }))
      },

      async onOpenCreateMovimentacao(selectedTp) {
        if (!state.result) {
          return
        }

        const tipoOperacaoMovimentacao = getTipoOperacaoMovimentacao(state.result)

        if (!tipoOperacaoMovimentacao) {
          setState(prevState => ({
            ...prevState,
            errorMessage: ZERO_DIFERENCA_ERROR,
          }))
          return
        }

        setState(prevState => ({
          ...prevState,
          ...resetCreateMovimentacaoState({
            showCreateMovimentacaoModal: true,
            loadingOperacoesMovimentacao: true,
            createMovimentacaoForm: createInitialCreateMovimentacaoForm(state.result),
          }),
        }))

        try {
          const operacoes = await EstoqueHttp.getOperacoesEstoque(selectedTp)
          setState(prevState => ({
            ...prevState,
            operacoesMovimentacao: buildOperacoes(operacoes).filter(
              operacao => operacao.type === tipoOperacaoMovimentacao,
            ),
            loadingOperacoesMovimentacao: false,
          }))
        } catch (error) {
          setState(prevState => ({
            ...prevState,
            loadingOperacoesMovimentacao: false,
            errorMessage: errorHandler(error),
          }))
        }
      },

      onCloseCreateMovimentacao() {
        setState(prevState => ({
          ...prevState,
          ...resetCreateMovimentacaoState(),
        }))
      },

      async onEstoqueMovimentacaoChange(selectedTp, selectedEstoque) {
        setState(prevState => ({
          ...prevState,
          createMovimentacaoForm: {
            ...prevState.createMovimentacaoForm,
            selectedEstoque,
            selectedPosicaoEstoque: null,
          },
          posicoesMovimentacao: [],
          loadingPosicoesMovimentacao: !!selectedEstoque,
        }))

        if (!selectedEstoque) {
          return
        }

        try {
          const posicoes = await EstoqueHttp.getPosicoesEstoquesById(selectedTp, selectedEstoque.id)
          const sortedPosicoes = [...posicoes].sort((a, b) => {
            if (a.sequencia !== b.sequencia) return Number(a.sequencia) - Number(b.sequencia)
            return a.endereco.localeCompare(b.endereco)
          })

          setState(prevState => ({
            ...prevState,
            posicoesMovimentacao: sortedPosicoes,
            loadingPosicoesMovimentacao: false,
          }))
        } catch (error) {
          setState(prevState => ({
            ...prevState,
            loadingPosicoesMovimentacao: false,
            errorMessage: errorHandler(error),
          }))
        }
      },

      onOpenConfirmCriacao() {
        const validationError = validateCreateMovimentacaoForm(state.createMovimentacaoForm)

        if (validationError) {
          setState(prevState => ({
            ...prevState,
            errorMessage: validationError,
          }))
          return
        }

        setState(prevState => ({
          ...prevState,
          showConfirmCriacaoModal: true,
        }))
      },

      onCloseConfirmCriacao() {
        setState(prevState => ({
          ...prevState,
          showConfirmCriacaoModal: false,
        }))
      },

      async onConfirmCriacaoMovimentacao(selectedTp) {
        const validationError = validateCreateMovimentacaoForm(state.createMovimentacaoForm)

        if (validationError) {
          setState(prevState => ({
            ...prevState,
            errorMessage: validationError,
          }))
          return
        }

        const { selectedEmpresa, selectedProduto, selectedEstoques, createMovimentacaoForm } = state

        setState(prevState => ({
          ...prevState,
          loadingMessage: LOAD_MESSAGE.SALVAR,
          savingMovimentacao: true,
        }))

        try {
          const dataMovimentacao = `${parseToMysqlFormat(
            createMovimentacaoForm.dataMovimentacao,
          )} ${createMovimentacaoForm.horaMovimentacao}:00`

          const result = await SystemHttp.createMovimentacaoSaldoMovimentacoesSaldoEstoque(
            selectedTp,
            selectedEmpresa.id,
            selectedProduto.id,
            selectedEstoques.map(estoque => estoque.id),
            createMovimentacaoForm.selectedEstoque.id,
            createMovimentacaoForm.selectedPosicaoEstoque.id,
            createMovimentacaoForm.selectedOperacaoEstoque.id,
            Math.abs(Number(createMovimentacaoForm.quantidade)),
            dataMovimentacao,
          )

          setState(prevState => ({
            ...prevState,
            result,
            successMessage: 'Movimentação criada com sucesso',
            loadingMessage: LOAD_MESSAGE.NONE,
            ...resetCreateMovimentacaoState(),
          }))
        } catch (error) {
          setState(prevState => ({
            ...prevState,
            loadingMessage: LOAD_MESSAGE.NONE,
            savingMovimentacao: false,
            errorMessage: errorHandler(error),
          }))
        }
      },

      async onPesquisar(selectedTp) {
        const { selectedEstoques, selectedEmpresa, selectedProduto } = state

        if (!selectedTp || selectedTp === 0) {
          setState(prevState => ({
            ...prevState,
            errorMessage: 'Selecione um TP para utilizar esta tela.',
          }))
          return
        }

        if (!selectedEstoques.length) {
          setState(prevState => ({
            ...prevState,
            errorMessage: 'Selecione ao menos um estoque.',
          }))
          return
        }

        if (!selectedEmpresa) {
          setState(prevState => ({
            ...prevState,
            errorMessage: 'Selecione uma empresa.',
          }))
          return
        }

        if (!selectedProduto) {
          setState(prevState => ({
            ...prevState,
            errorMessage: 'Selecione um produto.',
          }))
          return
        }

        setState(prevState => ({
          ...prevState,
          loadingMessage: LOAD_MESSAGE.PESQUISA,
          errorMessage: null,
          ...resetCreateMovimentacaoState(),
        }))

        try {
          const result = await SystemHttp.getSaldoMovimentacoesSaldoEstoque(
            selectedTp,
            selectedEmpresa.id,
            selectedProduto.id,
            selectedEstoques.map(estoque => estoque.id),
          )

          setState(prevState => ({
            ...prevState,
            result,
            loadingMessage: LOAD_MESSAGE.NONE,
          }))
        } catch (error) {
          setState(prevState => ({
            ...prevState,
            loadingMessage: LOAD_MESSAGE.NONE,
            errorMessage: errorHandler(error),
          }))
        }
      },
    }
  }, [state])

  return { state, localDispatch }
}
